import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import {
  X,
  ChevronDown,
  Calendar,
  Flag,
  Tag,
  User,
  Layers,
  RefreshCw,
  Boxes,
  Zap,
  Target,
  GitBranch,
} from 'lucide-react';

interface CreateIssueModalProps {
  projectId: string;
  workspaceId: string;
  onClose: () => void;
  onSuccess?: (issue: any) => void;
  defaultStateId?: string;
}

const PRIORITY_OPTIONS = [
  { value: 1, label: 'None', color: 'bg-gray-400' },
  { value: 2, label: 'Low', color: 'bg-green-500' },
  { value: 3, label: 'Medium', color: 'bg-yellow-500' },
  { value: 4, label: 'High', color: 'bg-orange-500' },
  { value: 5, label: 'Urgent', color: 'bg-red-500' },
];

export function CreateIssueModal({
  projectId,
  workspaceId,
  onClose,
  onSuccess,
  defaultStateId,
}: CreateIssueModalProps) {
  const api = useApi();
  const queryClient = useQueryClient();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stateId, setStateId] = useState(defaultStateId || '');
  const [priority, setPriority] = useState(1);
  const [assignee, setAssignee] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [epicId, setEpicId] = useState('');
  const [parentId, setParentId] = useState('');
  const [estimate, setEstimate] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [showMore, setShowMore] = useState(false);

  // Fetch states and labels
  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId),
    enabled: !!projectId,
  });

  const { data: labels } = useQuery({
    queryKey: ['labels', projectId],
    queryFn: () => api.getLabels(projectId),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.getMembers(workspaceId),
    enabled: !!workspaceId,
  });

  const { data: cycles } = useQuery({
    queryKey: ['cycles', projectId],
    queryFn: () => api.getCycles(projectId),
    enabled: !!projectId,
  });

  const { data: modules } = useQuery({
    queryKey: ['modules', projectId],
    queryFn: () => api.getModules(projectId),
    enabled: !!projectId,
  });

  const { data: epics } = useQuery({
    queryKey: ['epics', projectId],
    queryFn: () => api.getEpics(projectId),
    enabled: !!projectId,
  });

  const { data: workItems } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId),
    enabled: !!projectId,
  });

  // Set default state when states load
  useEffect(() => {
    if (states && states.length > 0 && !stateId) {
      // Find "Todo" or first unstarted state, otherwise use first state
      const defaultState = states.find((s: any) => s.bh_group === 2) || states[0];
      setStateId(defaultState.bh_stateid);
    }
  }, [states, stateId]);

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createWorkItem(data),
    onSuccess: (newIssue) => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
      onSuccess?.(newIssue);
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !stateId) return;

    createMutation.mutate({
      bh_name: title.trim(),
      bh_description: description,
      bh_project: projectId,
      bh_state: stateId,
      bh_priority: priority,
      bh_assignee: assignee || null,
      bh_cycle: cycleId || null,
      bh_module: moduleId || null,
      bh_epic: epicId || null,
      bh_parent: parentId || null,
      bh_estimate: estimate,
      bh_startdate: startDate || null,
      bh_targetdate: targetDate || null,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Cmd/Ctrl + Enter to submit
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e as any);
    }
    // Escape to close
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const getStateById = (id: string) => states?.find((s: any) => s.bh_stateid === id);
  const selectedState = getStateById(stateId);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
          onKeyDown={handleKeyDown}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Create Issue</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-4">
              {/* Title */}
              <div>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Issue title"
                  className="w-full text-lg font-medium px-0 py-2 border-0 border-b-2 border-transparent focus:border-primary-500 focus:outline-none focus:ring-0 placeholder-gray-400"
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm"
                />
              </div>

              {/* Quick Properties Row */}
              <div className="flex flex-wrap items-center gap-3">
                {/* State */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">State</span>
                  <div className="relative">
                    <select
                      value={stateId}
                      onChange={(e) => setStateId(e.target.value)}
                      className="appearance-none pl-6 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white cursor-pointer"
                    >
                      {states?.map((state: any) => (
                        <option key={state.bh_stateid} value={state.bh_stateid}>
                          {state.bh_name}
                        </option>
                      ))}
                    </select>
                    <span
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
                      style={{ backgroundColor: selectedState?.bh_color || '#6b7280' }}
                    />
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Priority */}
                <div className="flex items-center gap-2">
                  <Flag className="w-4 h-4 text-gray-400" />
                  <select
                    value={priority}
                    onChange={(e) => setPriority(parseInt(e.target.value))}
                    className="appearance-none pl-2 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white cursor-pointer"
                  >
                    {PRIORITY_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Assignee */}
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <select
                    value={assignee}
                    onChange={(e) => setAssignee(e.target.value)}
                    className="appearance-none pl-2 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white cursor-pointer"
                  >
                    <option value="">Unassigned</option>
                    {members?.map((member: any) => (
                      <option key={member.bh_memberid} value={member.bh_memberid}>
                        {member.bh_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Toggle More Options */}
                <button
                  type="button"
                  onClick={() => setShowMore(!showMore)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {showMore ? 'Less options' : 'More options'}
                </button>
              </div>

              {/* Extended Options */}
              {showMore && (
                <div className="pt-4 border-t border-gray-100 space-y-4">
                  {/* Planning Row */}
                  <div className="flex flex-wrap gap-4">
                    {/* Cycle */}
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Cycle</span>
                      <select
                        value={cycleId}
                        onChange={(e) => setCycleId(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">No cycle</option>
                        {cycles?.map((cycle: any) => (
                          <option key={cycle.bh_cycleid} value={cycle.bh_cycleid}>
                            {cycle.bh_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Module */}
                    <div className="flex items-center gap-2">
                      <Boxes className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Module</span>
                      <select
                        value={moduleId}
                        onChange={(e) => setModuleId(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">No module</option>
                        {modules?.map((module: any) => (
                          <option key={module.bh_moduleid} value={module.bh_moduleid}>
                            {module.bh_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Epic */}
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Epic</span>
                      <select
                        value={epicId}
                        onChange={(e) => setEpicId(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">No epic</option>
                        {epics?.map((epic: any) => (
                          <option key={epic.bh_epicid} value={epic.bh_epicid}>
                            {epic.bh_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Hierarchy and Estimate Row */}
                  <div className="flex flex-wrap gap-4">
                    {/* Parent Issue */}
                    <div className="flex items-center gap-2">
                      <GitBranch className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Parent</span>
                      <select
                        value={parentId}
                        onChange={(e) => setParentId(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[180px]"
                      >
                        <option value="">No parent</option>
                        {workItems?.filter((wi: any) => !wi.bh_parent).map((wi: any) => (
                          <option key={wi.bh_workitemid} value={wi.bh_workitemid}>
                            #{wi.bh_sequenceid} {wi.bh_name.substring(0, 25)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Estimate */}
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Estimate</span>
                      <select
                        value={estimate || ''}
                        onChange={(e) => setEstimate(e.target.value ? parseInt(e.target.value) : null)}
                        className="px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">None</option>
                        <option value="1">1 point</option>
                        <option value="2">2 points</option>
                        <option value="3">3 points</option>
                        <option value="5">5 points</option>
                        <option value="8">8 points</option>
                        <option value="13">13 points</option>
                        <option value="21">21 points</option>
                      </select>
                    </div>
                  </div>

                  {/* Dates Row */}
                  <div className="flex flex-wrap gap-4">
                    {/* Start Date */}
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Start</span>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    {/* Due Date */}
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Due</span>
                      <input
                        type="date"
                        value={targetDate}
                        onChange={(e) => setTargetDate(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  {/* Labels */}
                  {labels && labels.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Labels</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {labels.map((label: any) => (
                          <button
                            key={label.bh_labelid}
                            type="button"
                            className="px-2 py-1 rounded-full text-xs font-medium text-white opacity-60 hover:opacity-100 transition-opacity"
                            style={{ backgroundColor: label.bh_color }}
                          >
                            {label.bh_name}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Labels can be added after creation
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <p className="text-xs text-gray-400">
                <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">⌘</kbd>
                {' + '}
                <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">Enter</kbd>
                {' to submit'}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!title.trim() || !stateId || createMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Issue'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
